import { AxiosError } from 'axios';
import { useState, useCallback } from 'react';

import {
  ErrorHttpResponse,
  HttpConfig,
  SuccessHttpResponse,
} from '../../models/https';
import useHttps from '../useHttps';
import { useAppDisPatch } from '../../redux/store';
import { addMatch } from '../../redux/slices/interviewMatchSlice';
import { IApplicantData } from '../../models/jobs/singleJobApplicants.model';

const useGetSingleJobApplicants = () => {
  const request = useHttps();
  const [loading, setLoading] = useState(false);
  const [applicantData, setApplicantData] = useState<
    IApplicantData[] | undefined
  >(undefined);

  const dispatch = useAppDisPatch();

  const onGetSingleJobApplicantsSuccess = useCallback(
    ({ data, message }: SuccessHttpResponse<IApplicantData[]>) => {
      console.log(data);
      const filteredData = data.filter(
        (applicant) => applicant.status === 'SUBMITTED'
      );
      setApplicantData(filteredData);

      // Filter applicants with status 'INTERVIEW'
      const interviewApplicants = data.filter(
        (applicant) => applicant.status === 'INTERVIEW'
      );

      // Dispatch each applicant's email and userId to Redux
      interviewApplicants.forEach((applicant) => {
        dispatch(
          addMatch({
            email: applicant.email,
            userId: applicant.userId,
          })
        );
      });
    },
    [dispatch]
  );

  const onGetSingleJobApplicantsError = useCallback(
    (err: AxiosError<ErrorHttpResponse>) => {
      if (
        err.response?.status === 404 &&
        err.response?.data?.message === 'No application found'
      ) {
        // Special-case: simulate a success response with empty data.
        onGetSingleJobApplicantsSuccess({
          status: 'success',
          data: [],
          message: 'No application found',
        });
        setLoading(false);
        return;
      }
      console.error(err?.message ?? 'Something went wrong while fetching jobs');
    },
    [onGetSingleJobApplicantsSuccess]
  );

  const getSingleJobApplicant = useCallback(
    (jobId: string) => {
      const url: HttpConfig = {
        url: `jobs-service/jobs/${jobId}/applicants`,
        method: 'get',
      };

      return request(
        url,
        onGetSingleJobApplicantsSuccess,
        setLoading,
        onGetSingleJobApplicantsError
      );
    },
    [request, onGetSingleJobApplicantsSuccess, onGetSingleJobApplicantsError]
  );

  return { getSingleJobApplicant, loading, applicantData };
};

export default useGetSingleJobApplicants;
